'use client'

import { ColumnDef } from '@tanstack/react-table'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import { ChevronUp, ChevronDown, ChevronsUpDown, Circle } from 'lucide-react'
import { Checkbox } from '../../../components/ui/checkbox'

export type Device = {
    id: string
    name: string
    serial: string
    cloudActive: boolean
    cloudActiveOverride: boolean
    onlineStatus: 'online' | 'offline' | 'error'
    business: { id: string; name: string }
    model: { id: string; name: string }
    softwareVersion: { id: number; name: string } | null
}

const getStatusColor = (onlineStatus: string) => {
    switch (onlineStatus) {
        case 'online':
            return 'bg-green-100 text-green-800'
        case 'offline':
            return 'bg-red-100 text-red-800'
        default:
            return 'bg-gray-100 text-gray-800'
    }
}

const SortableHeader = ({ column, label }: { column: any; label: string }) => (
    <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
        {label}
        {column.getIsSorted() === 'asc' ? (
            <ChevronUp className="ml-2 h-4 w-4" />
        ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown className="ml-2 h-4 w-4" />
        ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4" />
        )}
    </Button>
)

export const selectColumn: ColumnDef<Device> = {
    id: 'select',
    header: ({ table }) => (
        <Checkbox
            checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
        />
    ),
    cell: ({ row }) => (
        <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            onClick={(e) => e.stopPropagation()}
        />
    ),
    enableSorting: false,
    enableHiding: false,
}

export const baseColumns: ColumnDef<Device>[] = [
    {
        accessorKey: 'name',
        header: ({ column }) => (
            <SortableHeader column={column} label="Device Name" />
        ),
    },
    {
        accessorKey: 'serial',
        header: 'Serial Number',
    },
    {
        header: 'Business',
        accessorKey: 'business.name',
        id: 'businessName',
    },
    {
        header: 'Model',
        accessorKey: 'model.name',
        id: 'modelName',
    },
    {
        header: 'Status',
        accessorKey: 'onlineStatus',
        cell: ({ row }) => {
            const status = row.getValue('onlineStatus') as string
            return (
                <div className="flex items-center gap-2">
                    <Circle
                        className={`h-3 w-3 ${status === 'online' ? 'fill-green-500 text-green-500' : status === 'error' ? 'fill-red-500 text-red-500' : 'fill-gray-400 text-gray-400'}`}
                    />
                    <span className="capitalize">{status}</span>
                </div>
            )
        },
    },
    {
        header: 'Software Version',
        accessorKey: 'softwareVersion.name',
        id: 'softwareVersionName',
        cell: ({ row }) => {
            const sv = row.original.softwareVersion
            return <span className="text-sm">{sv?.name || '—'}</span>
        },
    },
]

export const cloudActiveColumn: ColumnDef<Device> = {
    header: 'Cloud Active',
    id: 'cloudActive',
    accessorFn: (row) => row.cloudActive || row.cloudActiveOverride,
    cell: ({ row }) => {
        const { cloudActive, cloudActiveOverride } = row.original

        return (
            <div className="flex items-center gap-1.5">
                <Badge
                    variant="secondary"
                    className={
                        cloudActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                    }
                >
                    {cloudActive ? 'Active' : 'Inactive'}
                </Badge>
                {cloudActiveOverride && (
                    <Badge
                        variant="secondary"
                        className="bg-amber-100 text-amber-700"
                    >
                        Overriden
                    </Badge>
                )}
            </div>
        )
    },
}
